import { TierNumber } from "@utils/itemQualityTiers";

import { dpApiV2, normalizeData,NormalizedData } from "./apiV2";
import charitiesApi from "./charities";

export type CreateItemAlertRequest = {
  item: string;
  searched_item: string;
  location: string;
  email: string;
  dropoff: boolean;
  postal: boolean;
}

export type ItemQualityTierResponse = {
  id: number;
  charity: number;
  item: number;
  tier: TierNumber;
};

export type Item = {
  id: number;
  name: string;
}

const extendedApi = dpApiV2.injectEndpoints({
  endpoints: builder => ({
    createItemAlert: builder.mutation<
      object,
      CreateItemAlertRequest
    >({
      query: body => ({
        url: "/items/alert/",
        method: "POST",
        body
      })
    }),
    createItem: builder.mutation<Item, { name: string }>({
      query: body => ({
        url: "items/items/",
        method: "POST",
        body
      }),
      invalidatesTags: [ "Item" ]
    }),
    getAllItems: builder.query<NormalizedData<Item>, string>({
      query: () => ({
        url: "items/items/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData<Item>(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Item" ]
    }),
    getActiveItems: builder.query<NormalizedData<Item>, string>({
      query: () => ({
        url: "items/public/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData<Item>(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Item" ]
    }),
    updateItemQualityTier: builder.mutation<ItemQualityTierResponse, { id: number, tier: TierNumber }>({
      query: body => ({
        method: "PATCH",
        url: `items/item-mappings/${body.id}/`,
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            charitiesApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => {
                const updatedItem = draft.items.find(item => item.id === patch.id);
                if (updatedItem) updatedItem.tier = patch.tier;
              }
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    createItemQualityTier: builder.mutation<ItemQualityTierResponse, { charity: number, item: number, tier: TierNumber }>({
      query: body => ({
        method: "POST",
        url: "items/item-mappings/",
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            charitiesApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => {
                const fakeId = 1e10 + Math.ceil(Math.random() * 10000);
                draft.items.push({
                  id: fakeId,
                  item: patch.item,
                  charity: patch.charity,
                  tier: patch.tier,
                  name: "..." // We need to put something for typing, but usually we want to extract the name from the `item` ID
                });
              }
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    deleteItemQualityTier: builder.mutation<ItemQualityTierResponse, number>({
      query: id => ({
        method: "DELETE",
        url: `items/item-mappings/${id}/`,
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        idToDelete,
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            charitiesApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => {
                const idIndex = draft.items.map(item => item.id).indexOf(idToDelete);
                draft.items.splice(idIndex, 1);
              }
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    })
  }),
  overrideExisting: true
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateItemMutation,
  useGetAllItemsQuery,
  useGetActiveItemsQuery,
  useCreateItemAlertMutation,
  useCreateItemQualityTierMutation,
  useUpdateItemQualityTierMutation,
  useDeleteItemQualityTierMutation
} = extendedApi;
